import { useCallback } from 'react';

/**
 * @description
 * Manages the state logic for location filters (regions, regional units, municipalities) on delete.
 * Handles:
 * - Automatic clearing of dependent filters when parent filters are cleared.
 *
 * @param {Object} props - Hook props object.
 * @param {Array} props.selectedRegions - Selected region values.
 * @param {Array} props.selectedRegionalUnits - Selected regional unit values.
 * @param {Array} props.selectedMunicipalities - Selected municipalities values.
 * @param {Function} props.setFilters - Dispatch function to update filters.
 * @returns {Object} An object containing three handlers:
 *  - handleRemoveRegion(code: string): removes a region and clears dependent regional units and municipalities.
 *  - handleRemoveRegionalUnit(code: string): removes a regional unit and clears dependent municipalities.
 *  - handleRemoveMunicipality(code: string): removes a municipality.
 */
export function useFilterRegionAutomaticClear({
  selectedRegions,
  selectedRegionalUnits,
  selectedMunicipalities,
  setFilters,
}) {
  const handleRemoveRegion = useCallback(
    code => {
      const newSelectedRegions = selectedRegions.filter(
        item => item.code !== code
      );
      const newSelectedRegionalUnits = selectedRegionalUnits.filter(
        selectedRegionalUnit =>
          newSelectedRegions.some(
            newSelectedRegion =>
              newSelectedRegion.code === selectedRegionalUnit.parentCode
          )
      );
      const newSelectedMunicipalities = selectedMunicipalities.filter(
        selectedMunicipality =>
          newSelectedRegionalUnits.some(
            newSelectedRegionalUnit =>
              newSelectedRegionalUnit.code === selectedMunicipality.parentCode
          )
      );
      setFilters({
        type: 'SET_SELECTED_MUNICIPALITY',
        value: newSelectedMunicipalities,
      });
      setFilters({
        type: 'SET_SELECTED_REGION_UNITY',
        value: newSelectedRegionalUnits,
      });
      setFilters({
        type: 'SET_SELECTED_REGION_HQ',
        value: newSelectedRegions,
      });
    },
    [selectedMunicipalities, selectedRegionalUnits, selectedRegions, setFilters]
  );

  const handleRemoveRegionalUnit = useCallback(
    code => {
      const newSelectedRegionalUnits = selectedRegionalUnits.filter(
        item => item.code !== code
      );
      const newSelectedMunicipalities = selectedMunicipalities.filter(
        selectedMunicipality =>
          newSelectedRegionalUnits.some(
            newSelectedRegionalUnit =>
              newSelectedRegionalUnit.code === selectedMunicipality.parentCode
          )
      );
      setFilters({
        type: 'SET_SELECTED_MUNICIPALITY',
        value: newSelectedMunicipalities,
      });
      setFilters({
        type: 'SET_SELECTED_REGION_UNITY',
        value: newSelectedRegionalUnits,
      });
    },
    [selectedMunicipalities, selectedRegionalUnits, setFilters]
  );

  const handleRemoveMunicipality = useCallback(
    code => {
      const newSelectedMunicipalities = selectedMunicipalities.filter(
        item => item.code !== code
      );
      setFilters({
        type: 'SET_SELECTED_MUNICIPALITY',
        value: newSelectedMunicipalities,
      });
    },
    [selectedMunicipalities, setFilters]
  );

  return {
    handleRemoveMunicipality,
    handleRemoveRegionalUnit,
    handleRemoveRegion,
  };
}
